import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Check, Loader2, ShieldCheck, Clock, ArrowLeft, Crown, Sparkles } from "lucide-react";
import { fetchProgramasLicenciables, fetchMiLicencia, checkoutLicencia } from "../../lib/pagos";

const DUR_META = {
  m1: { meses: 1, label: "1 mes", nota: "Mensual" },
  m3: { meses: 3, label: "3 meses", nota: "Precio base" },
  m6: { meses: 6, label: "6 meses", nota: null },
  m12: { meses: 12, label: "12 meses", nota: null },
};

function fmtFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "long", year: "numeric" });
}

export default function LicenciaCheckout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [prog, setProg] = useState(null);
  const [licencia, setLicencia] = useState(null);
  const [sel, setSel] = useState("m6");
  const [loading, setLoading] = useState(true);
  const [comprando, setComprando] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [progs, lic] = await Promise.all([
          fetchProgramasLicenciables(),
          fetchMiLicencia(slug).catch(() => null),
        ]);
        if (!alive) return;
        setProg((progs || []).find((p) => p.slug === slug) || null);
        setLicencia(lic);
      } catch (e) {
        if (alive) setErr("No se pudo cargar la información del programa.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const opciones = useMemo(() => {
    const pr = prog?.precios;
    if (!pr) return [];
    return ["m1", "m3", "m6", "m12"].map((k) => ({
      key: k, ...DUR_META[k],
      soles: pr[k]?.soles, centimos: pr[k]?.centimos,
      ahorro: pr[k]?.ahorro_pct || 0,
    })).filter((o) => Number(o.centimos) > 0);
  }, [prog]);

  const comprar = async () => {
    setComprando(true); setErr("");
    try {
      const { url } = await checkoutLicencia(slug, DUR_META[sel].meses);
      if (url) window.location.href = url;
      else { setErr("No se pudo iniciar el pago."); setComprando(false); }
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo iniciar el pago.");
      setComprando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] grid place-items-center" data-testid="licencia-loading">
        <Loader2 size={36} className="animate-spin text-brand-blue" />
      </div>
    );
  }

  if (!prog) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6 text-center" data-testid="licencia-no-prog">
        <div>
          <p className="text-slate-600 font-semibold">Este programa no está disponible para licencia.</p>
          <Link to="/software/vivienda-multifamiliar" className="mt-4 inline-flex items-center gap-2 text-brand-blue font-semibold hover:underline">
            <ArrowLeft size={16} /> Volver
          </Link>
        </div>
      </div>
    );
  }

  const vigente = licencia?.estado === "vigente";

  return (
    <div className="min-h-[80vh] px-5 py-10 bg-slate-50" data-testid="licencia-checkout-page">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-6" data-testid="licencia-volver">
          <ArrowLeft size={16} /> Volver
        </button>

        {/* Tarjeta del programa */}
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-7">
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-2xl bg-indigo-50 grid place-items-center shrink-0">
              <Crown size={24} className="text-indigo-600" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-extrabold text-slate-900" data-testid="licencia-prog-nombre">{prog.name}</h1>
                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">Programa Especializado</span>
              </div>
              <p className="mt-1 text-slate-500 text-sm">{prog.descripcion || prog.subtitulo || "Licencia de acceso completo al programa durante la vigencia seleccionada."}</p>
            </div>
          </div>

          {/* Estado de licencia actual */}
          {vigente && (
            <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3" data-testid="licencia-estado-vigente">
              <ShieldCheck size={20} className="text-emerald-600 shrink-0" />
              <div className="text-sm">
                <span className="font-bold text-emerald-800">Licencia ACTIVA</span>
                <span className="text-emerald-700"> · vence el {fmtFecha(licencia.fecha_vencimiento)} ({licencia.dias_restantes} días)</span>
                <span className="block text-emerald-600 text-xs">Al comprar de nuevo, el tiempo se suma a tu vigencia actual.</span>
              </div>
            </div>
          )}
        </div>

        {/* Membresía ≠ Licencia */}
        <div className="mt-4 rounded-xl bg-indigo-50 border border-indigo-100 px-4 py-2.5 text-[12px] text-indigo-800 flex items-center gap-2" data-testid="licencia-aviso">
          <Sparkles size={14} /> La <b>Licencia del Programa</b> es independiente de tu Membresía (Básico/Pro/Premium). La membresía no aplica descuentos sobre licencias.
        </div>

        {/* Selección de duración */}
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {opciones.map((o) => {
            const active = sel === o.key;
            return (
              <button key={o.key} onClick={() => setSel(o.key)} data-testid={`licencia-opcion-${o.key}`}
                className="relative text-left rounded-2xl border-2 p-5 transition-all"
                style={active ? { borderColor: "#4f46e5", background: "#eef2ff" } : { borderColor: "#e2e8f0", background: "#fff" }}>
                {o.ahorro > 0 && (
                  <span className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-extrabold" data-testid={`licencia-ahorro-${o.key}`}>
                    AHORRA {o.ahorro}%
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <Clock size={15} className={active ? "text-indigo-600" : "text-slate-400"} />
                  <span className="font-bold text-slate-800">{o.label}</span>
                  {active && <Check size={16} className="ml-auto text-indigo-600" />}
                </div>
                <div className="mt-3 text-2xl font-extrabold text-slate-900" data-testid={`licencia-precio-${o.key}`}>S/ {o.soles}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{o.nota || "Acceso completo · sin recortes"}</div>
              </button>
            );
          })}
        </div>

        {err && <p className="mt-4 text-sm text-red-600 font-medium" data-testid="licencia-error">{err}</p>}

        <button onClick={comprar} disabled={comprando} data-testid="licencia-comprar-btn"
          className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-8 py-3.5 transition-colors disabled:opacity-60">
          {comprando ? <Loader2 size={18} className="animate-spin" /> : <Crown size={18} />}
          {vigente ? "RENOVAR LICENCIA" : "COMPRAR LICENCIA"} · S/ {opciones.find((o) => o.key === sel)?.soles}
        </button>
        <p className="mt-3 text-[11px] text-slate-400">Pago seguro con Mercado Pago · La licencia se activa automáticamente al aprobarse el pago.</p>
      </div>
    </div>
  );
}
